import { useEffect, useMemo, useState } from "react";
import { Eye, Loader2, Search, Download, Filter, MapPin, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../../lib/api";
import { styleFor } from "../../lib/leadKindStyles";
import { formatReceivedAt } from "../../lib/dateFormat";
import LeadDetailModal from "./LeadDetailModal";

const STATUS_BADGE = {
    new: "bg-amber-100 text-amber-800 border-amber-200",
    contacted: "bg-blue-100 text-blue-700 border-blue-200",
    in_progress: "bg-indigo-100 text-indigo-700 border-indigo-200",
    converted: "bg-emerald-100 text-emerald-700 border-emerald-200",
    lost: "bg-slate-100 text-slate-600 border-slate-200",
    under_review: "bg-blue-100 text-blue-700 border-blue-200",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
};

const STATUS_LABEL = {
    new: "New", contacted: "Contacted", in_progress: "In Progress",
    converted: "Converted", lost: "Lost", under_review: "Under Review",
    approved: "Approved", rejected: "Rejected",
};

const FILTERS = {
    leads: ["all", "new", "contacted", "in_progress", "converted", "lost"],
    tutors: ["all", "new", "under_review", "approved", "rejected"],
};

export default function LeadsView({ kind, title, autoOpen }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [view, setView] = useState(null);
    // V25 — admin city + mode filters (Group A items 2 & 3)
    const [cityFilter, setCityFilter] = useState("all");
    const [modeFilter, setModeFilter] = useState("all");
    // V144 — WA-reply activity filter: "all" | "sent" | "not_sent"
    const [waReplyFilter, setWaReplyFilter] = useState("all");
    // V151 — State chip row: "all" | <state name>
    const [stateFilter, setStateFilter] = useState("all");
    const [stateCounts, setStateCounts] = useState([]);
    const [availableCities, setAvailableCities] = useState([]);
    // V135.1 — auto-open a specific record when arriving from Universal Search
    const [pendingAutoOpen, setPendingAutoOpen] = useState(null);
    useEffect(() => {
        if (!autoOpen || !autoOpen.id) return;
        // Reset filters so the target record can be found regardless of its status
        setStatusFilter("all");
        setWaReplyFilter("all");
        setStateFilter("all");
        setSearch("");
        setPendingAutoOpen(autoOpen);
    }, [autoOpen?.id, autoOpen?.seq]);

    const isTutor = kind === "tutor-applications";
    const filterKey = isTutor ? "tutors" : "leads";
    const showLeadFilters = kind === "registrations" || kind === "inquiries";

    // Pull persisted default city filter (used as initial value for the dropdown)
    useEffect(() => {
        if (!showLeadFilters) return;
        api.get("/admin/tutor-enquiry-visibility/settings")
            .then((r) => {
                const def = (r.data?.home_city_filter || "all");
                setCityFilter(def);
                setAvailableCities(Array.isArray(r.data?.available_cities) ? r.data.available_cities : []);
            })
            .catch(() => { /* default to 'all' on failure */ });
    }, [showLeadFilters]);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusFilter !== "all") params.append("status", statusFilter);
        if (search.trim()) params.append("search", search.trim());
        if (showLeadFilters && cityFilter && cityFilter !== "all") params.append("city", cityFilter);
        if (showLeadFilters && modeFilter && modeFilter !== "all") params.append("mode", modeFilter);
        if (showLeadFilters && waReplyFilter && waReplyFilter !== "all") params.append("wa_reply", waReplyFilter);
        if (showLeadFilters && stateFilter && stateFilter !== "all") params.append("state", stateFilter);
        api.get(`/admin/${kind}?${params.toString()}`)
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };

    useEffect(load, [kind, statusFilter, cityFilter, modeFilter, waReplyFilter, stateFilter]);

    // V151 — fetch state counts once per kind for the chip row
    useEffect(() => {
        if (!showLeadFilters) return;
        api.get(`/admin/lead-states?kind=${kind}`)
            .then((r) => setStateCounts(r.data?.items || []))
            .catch(() => setStateCounts([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);
    useEffect(() => {
        const t = setTimeout(load, search ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // V135.1 — once items load, if we have a pending auto-open target,
    // find the matching row and pop its detail modal. This fires whenever
    // items refresh or a new search-click arrives with a fresh `seq`.
    useEffect(() => {
        if (!pendingAutoOpen || !items?.length) return;
        const hit = items.find((it) => (it.id || it._id) === pendingAutoOpen.id);
        if (hit) {
            setView(hit);
            setPendingAutoOpen(null);
        }
    }, [items, pendingAutoOpen]);

    const exportCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter !== "all") params.append("status", statusFilter);
            if (search.trim()) params.append("search", search.trim());
            const token = localStorage.getItem("at_token");
            const res = await fetch(`${API_BASE}/admin/${kind}/export.csv?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch {
            toast.error("Export failed");
        }
    };

    const counts = useMemo(() => {
        const c = { all: items.length };
        items.forEach((i) => { c[i.status || "new"] = (c[i.status || "new"] || 0) + 1; });
        return c;
    }, [items]);

    return (
        <div data-testid={`admin-${kind}`} data-kind={kind}>
            {(() => { const ks = styleFor(kind); return (
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-start gap-3">
                    <span
                        data-testid={`leads-page-kind-chip-${kind}`}
                        className={`mt-2 inline-flex items-center text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${ks.badge}`}
                    >
                        {ks.short}
                    </span>
                    <div>
                        <h2 className="font-heading text-2xl font-bold">{title}</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{items.length} {items.length === 1 ? "record" : "records"}{statusFilter !== "all" ? ` · filtered by ${STATUS_LABEL[statusFilter] || statusFilter}` : ""}</p>
                    </div>
                </div>
                <button
                    onClick={exportCsv}
                    data-testid={`${kind}-export-csv`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-sm font-semibold text-slate-700 rounded-lg hover:border-blue-300 hover:text-[#1E3A8A]"
                >
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>
            ); })()}

            {/* search + filter chips */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 space-y-3">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, phone, email, city, message…"
                        data-testid={`${kind}-search`}
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    />
                </div>
                {showLeadFilters && (
                    <div className="flex flex-wrap gap-2" data-testid={`${kind}-lead-filters`}>
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                            <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                            <span className="uppercase tracking-wider font-semibold">City</span>
                            <select
                                value={cityFilter}
                                onChange={(e) => setCityFilter(e.target.value)}
                                data-testid={`${kind}-city-filter`}
                                className="text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                            >
                                <option value="all">All Cities</option>
                                {availableCities.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                            <Layers className="w-3.5 h-3.5 text-[#1E3A8A]" />
                            <span className="uppercase tracking-wider font-semibold">Mode</span>
                            <select
                                value={modeFilter}
                                onChange={(e) => setModeFilter(e.target.value)}
                                data-testid={`${kind}-mode-filter`}
                                className="text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                            >
                                <option value="all">All Modes</option>
                                <option value="home">Home Tuition</option>
                                <option value="online">Online Tuition</option>
                            </select>
                        </label>
                        {(cityFilter !== "all" || modeFilter !== "all" || waReplyFilter !== "all" || stateFilter !== "all") && (
                            <button
                                type="button"
                                onClick={() => { setCityFilter("all"); setModeFilter("all"); setWaReplyFilter("all"); setStateFilter("all"); }}
                                data-testid={`${kind}-clear-filters`}
                                className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:text-red-600 hover:border-red-200"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs uppercase tracking-[0.15em] text-slate-400 self-center mr-1 inline-flex items-center gap-1"><Filter className="w-3 h-3" /></span>
                    {FILTERS[filterKey].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            data-testid={`${kind}-filter-${s}`}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                                statusFilter === s
                                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                        >
                            {s === "all" ? "All" : STATUS_LABEL[s] || s}
                            {counts[s] !== undefined && <span className="ml-1.5 opacity-70">{counts[s]}</span>}
                        </button>
                    ))}
                </div>
                {/* V151 — State chip row: live from /admin/lead-states aggregation */}
                {showLeadFilters && stateCounts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`${kind}-state-chips`}>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 self-center mr-1 font-semibold">State</span>
                        <button
                            type="button"
                            onClick={() => setStateFilter("all")}
                            data-testid={`${kind}-state-filter-all`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                                stateFilter === "all"
                                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                        >All</button>
                        {stateCounts.map((s) => (
                            <button
                                key={s.state}
                                type="button"
                                onClick={() => setStateFilter(s.state)}
                                data-testid={`${kind}-state-filter-${s.state.replace(/\s+/g, "-")}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all inline-flex items-center gap-1 ${
                                    stateFilter === s.state
                                        ? "bg-indigo-600 text-white border-indigo-700"
                                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                }`}
                            >
                                {s.state}
                                <span className={`text-[9px] font-bold rounded-full px-1 ${stateFilter === s.state ? "bg-white/25" : "bg-slate-100 text-slate-500"}`}>{s.count}</span>
                            </button>
                        ))}
                    </div>
                )}
                {/* V144 — WhatsApp-reply activity chips (registrations + inquiries only) */}
                {showLeadFilters && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`${kind}-wa-reply-chips`}>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 self-center mr-1 font-semibold">WhatsApp reply</span>
                        {[
                            { id: "all",      label: "All" },
                            { id: "sent",     label: "Has reply" },
                            { id: "not_sent", label: "No reply" },
                        ].map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => setWaReplyFilter(c.id)}
                                data-testid={`${kind}-wa-reply-filter-${c.id}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                                    waReplyFilter === c.id
                                        ? (c.id === "sent"
                                            ? "bg-emerald-600 text-white border-emerald-700"
                                            : c.id === "not_sent"
                                                ? "bg-rose-600 text-white border-rose-700"
                                                : "bg-[#1E3A8A] text-white border-[#1E3A8A]")
                                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
                    No {title.toLowerCase()} found.
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Name</th>
                                    <th className="px-4 py-3 text-left">Contact</th>
                                    <th className="px-4 py-3 text-left">{kind === "registrations" ? "Grade · Board" : kind === "tutor-applications" ? "Subjects · Qual" : "Subject"}</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    {!isTutor && <th className="px-4 py-3 text-left">Assigned</th>}
                                    <th className="px-4 py-3 text-left">Date</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((it) => {
                                    // V154 — Unread visual: only demo-request kinds are read-tracked
                                    const isUnread = kind !== "tutor-applications" && !it.opened_at;
                                    return (
                                    <tr
                                        key={it.id}
                                        data-testid={`lead-row-${it.id}`}
                                        data-unread={isUnread ? "true" : "false"}
                                        className={
                                            isUnread
                                                ? "bg-amber-50/60 hover:bg-amber-50 border-l-4 border-l-amber-400"
                                                : "hover:bg-slate-50"
                                        }
                                    >
                                        <td className="px-4 py-3 font-medium text-[#0F172A]">
                                            <div className="flex items-center gap-2">
                                                {isUnread && (
                                                    <span
                                                        aria-hidden="true"
                                                        data-testid={`lead-unread-dot-${it.id}`}
                                                        className="w-2 h-2 rounded-full bg-amber-500 shrink-0"
                                                    />
                                                )}
                                                <span className={isUnread ? "font-bold" : ""}>{it.name}</span>
                                            </div>
                                            {it.notes_log && it.notes_log.length > 0 && (
                                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">{it.notes_log.length} note{it.notes_log.length > 1 ? "s" : ""}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">
                                            <div className="text-xs">{it.email}</div>
                                            <div className="text-xs text-slate-400">{it.phone}{it.city ? ` · ${it.city}` : ""}</div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 text-xs max-w-xs truncate">
                                            {kind === "registrations" ? `${it.grade || "—"}${it.board ? ` · ${it.board}` : ""}` :
                                             kind === "tutor-applications" ? `${it.subjects || "—"}${it.qualification ? ` · ${it.qualification}` : ""}` :
                                             (it.subject || it.message || "—")}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border ${STATUS_BADGE[it.status || "new"] || STATUS_BADGE.new}`}>
                                                {STATUS_LABEL[it.status || "new"] || it.status || "New"}
                                            </span>
                                        </td>
                                        {!isTutor && (
                                            <td className="px-4 py-3 text-xs text-slate-500">{it.assigned_to || <span className="text-slate-300">—</span>}</td>
                                        )}
                                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap" data-testid={`lead-received-${it.id}`}>{formatReceivedAt(it.created_at)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => setView(it)}
                                                data-testid={`view-${it.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#1E3A8A] bg-blue-50 hover:bg-blue-100 rounded-lg"
                                            >
                                                <Eye className="w-3.5 h-3.5" /> Open
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {view && (
                <LeadDetailModal
                    kind={kind}
                    item={view}
                    onClose={() => setView(null)}
                    onSaved={load}
                    onOpened={(id, openedAt, newStatus) => {
                        // V154 — optimistic in-place patch so the row flips to
                        // the "read" visual immediately, without waiting for
                        // the background load() refetch to race the DB write.
                        // V170.5 — also patches status when the server flipped
                        // "new" → "contacted" so the badge updates in-place.
                        setItems((prev) => prev.map((it) => (
                            it.id === id
                                ? { ...it, opened_at: openedAt, ...(newStatus ? { status: newStatus } : {}) }
                                : it
                        )));
                    }}
                />
            )}
        </div>
    );
}
